import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type AppLanguage = 'en' | 'ar';

const STORAGE_KEY = 'app_language';
const DEFAULT_LANGUAGE: AppLanguage = 'en';

export interface LanguageOption {
  code: AppLanguage;
  label: string;
  nativeLabel: string;
  dir: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
  { code: 'ar', label: 'Arabic',  nativeLabel: 'عربي',    dir: 'rtl' }
];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<AppLanguage>(DEFAULT_LANGUAGE);
  readonly currentLanguage$: Observable<AppLanguage> = this.currentLanguageSubject.asObservable();

  constructor(private translate: TranslateService) {
    translate.addLangs(SUPPORTED_LANGUAGES.map(l => l.code));
    translate.setDefaultLang(DEFAULT_LANGUAGE);
  }

  getStoredLanguage(): AppLanguage {
    const stored = localStorage.getItem(STORAGE_KEY) as AppLanguage | null;
    return SUPPORTED_LANGUAGES.some(l => l.code === stored) ? stored! : DEFAULT_LANGUAGE;
  }

  getCurrentLanguage(): AppLanguage {
    return this.currentLanguageSubject.value;
  }

  isRtl(): boolean {
    return this.getOption(this.getCurrentLanguage()).dir === 'rtl';
  }

  getOption(code: AppLanguage): LanguageOption {
    return SUPPORTED_LANGUAGES.find(l => l.code === code) || SUPPORTED_LANGUAGES[0];
  }

  applyLanguage(lang: AppLanguage): Promise<void> {
    const option = this.getOption(lang);
    document.documentElement.setAttribute('dir', option.dir);
    document.documentElement.setAttribute('lang', option.code);
    document.body.classList.toggle('rtl', option.dir === 'rtl');
    document.body.classList.toggle('ltr', option.dir === 'ltr');
    this.currentLanguageSubject.next(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    return new Promise<void>(resolve => {
      this.translate.use(lang).subscribe({
        next: () => resolve(),
        error: () => resolve()
      });
    });
  }

  setLanguage(lang: AppLanguage): void {
    this.applyLanguage(lang);
  }
}
